<template>
  <v-dialog
    :key="`report-dialog-${reportIds.join('-') || 'default'}`"
    :value="dialog"
    :width="modalSpecial.REPORT_SUMMARY_WIDTH"
    :max-width="modalSpecial.REPORT_SUMMARY_MAX_WIDTH"
    content-class="examine-report-summary-dialog-content"
    scrollable
    v-bind="modalPolicy.P2_FORM"
    :data-testid="getTestId('report-summary-dialog')"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    @close="closeDialog"
  >
    <v-card
      v-if="!error"
      ref="reportCard"
      class="report-dialog"
      :data-testid="getTestId('report-summary-card')"
    >
      <table class="report-container-table">
        <tr>
          <td class="report-header-cell">
            <div class="report-header" :data-testid="getTestId('report-header')">
              <div class="header-top">
                <div class="logo-section">
                  <SolvitLogoWhiteFull
                    class="company-logo"
                    :size="50"
                    :style="{ color: $vuetify.theme.currentTheme.text }"
                    aria-label="SOLVIT Logo"
                  />
                </div>
                <div class="title-section">
                  <h1 :data-testid="getTestId('report-title')">
                    {{ $t('reportSummary.title') }}
                  </h1>
                </div>
                <div v-if="!loading && reportSummary" class="document-info-section">
                  <table class="document-info-table">
                    <thead>
                      <tr>
                        <th class="info-header" colspan="2">
                          {{ $t('reportSummary.documentInfo') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="info-label">
                          {{ $t('reportSummary.inspectionOrg') }}
                        </td>
                        <td class="info-value">
                          {{
                            reportSummary?.inspectionCompanyName ||
                            $t('reportSummary.defaultInspectionOrg')
                          }}
                        </td>
                      </tr>
                      <tr>
                        <td class="info-label">
                          {{ $t('reportSummary.docNumber') }}
                        </td>
                        <td class="info-value">
                          {{ generateDocumentNumber() }}
                        </td>
                      </tr>
                      <tr>
                        <td class="info-label">
                          {{ $t('reportSummary.createdDate') }}
                        </td>
                        <td class="info-value">
                          {{ formatDate(reportSummary?.examineDate) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="buttons-section">
                    <v-btn
                      v-show="!loading && reportSummary"
                      outlined
                      class="solvit-crud-outline-btn solvit-crud-outline-btn--primary mr-2"
                      :loading="pdfLoading"
                      :data-testid="getTestId('pdf-download-button')"
                      @click="downloadPdf"
                    >
                      <v-icon left> mdi-file-pdf-box </v-icon>
                      {{ $t('reportSummary.pdfDownload') }}
                    </v-btn>
                    <v-btn
                      outlined
                      class="solvit-crud-outline-btn"
                      :data-testid="getTestId('report-close-button')"
                      @click="closeDialog"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>

        <tr>
          <td class="report-content-cell">
            <ReportCommonSections :report-summary="reportSummary" :loading="loading" />
          </td>
        </tr>
      </table>
    </v-card>
    <v-card v-else class="mx-auto" max-width="344">
      <v-img :src="require(`@/assets/image/solvit_logo.png`)" height="200px" />
      <CrudFormDialogHeader
        :kicker="$t('reportSummary.title')"
        :title="$t('reportSummary.errorTitle')"
      />
      <v-card-subtitle class="px-4">
        {{ $t('reportSummary.errorMessage') }}
      </v-card-subtitle>
    </v-card>
  </v-dialog>
</template>

<script>
  import { getTestId } from '@/utils/testUtils';
  import { usePdfDownload } from '@/utils/pdfUtils';
  import SolvitLogoWhiteFull from '@/components/brand/SolvitLogoWhiteFull.vue';
  import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
  import ReportCommonSections from '@/components/dialog/ReportCommonSections.vue';
  import { MODAL_LAYOUT_SPECIAL, MODAL_CLOSE_POLICY } from '@/constants/modalLayout';
  import { formatReportDate, parseReportDateParts } from '@/utils/reportLocaleUtils';

  export default {
    name: 'ReportSummaryDialog',
    components: {
      SolvitLogoWhiteFull,
      CrudFormDialogHeader,
      ReportCommonSections
    },
    props: {
      dialog: {
        type: Boolean,
        default: false
      },
      reportName: {
        type: String,
        required: true
      },
      reportIds: {
        type: Array,
        default: () => []
      },
      summaryData: {
        type: Object,
        default: null
      }
    },
    data: () => ({
      modalSpecial: MODAL_LAYOUT_SPECIAL,
      modalPolicy: MODAL_CLOSE_POLICY,
      error: false,
      loading: true,
      pdfLoading: false,
      reportSummary: null
    }),
    watch: {
      summaryData: {
        handler(newData) {
          if (newData) {
            this.reportSummary = newData;
            this.loading = false;
            this.error = false;
          }
        },
        immediate: true
      }
    },
    methods: {
      closeDialog() {
        this.$emit('close');
        this.$emit('update:dialog', false);
      },

      /**
       * 보고서 요소의 실제 크기 계산 (PDF용 최적화)
       */
      calculateElementSize(element) {
        const rect = element.getBoundingClientRect();
        const width = Math.max(element.scrollWidth, element.offsetWidth, rect.width);
        return { width, height: 'auto' };
      },

      /**
       * PDF 다운로드
       */
      async downloadPdf() {
        try {
          this.pdfLoading = true;

          const reportElement = this.$refs.reportCard.$el;
          if (!reportElement) {
            throw new Error(this.$t('common.reportElementNotFound'));
          }

          const { width } = this.calculateElementSize(reportElement);
          const { downloadReport } = usePdfDownload();

          const fontOptions = {
            baseFontSize: '12px',
            baseFontWeight: 'normal',
            headerFontSize: '18px',
            headerFontWeight: 'bold',
            subHeaderFontSize: '14px',
            subHeaderFontWeight: 'bold',
            tableFontSize: '11px',
            tableFontWeight: 'normal',
            tableHeaderFontWeight: 'bold',
            lineHeight: '1.4',
            fontFamily: 'Arial, sans-serif',
            useCORS: true,
            scale: 2,
            width: width,
            backgroundColor: '#ffffff',
            displayHeaderFooter: true,
            headerTemplate: '<div></div>',
            footerTemplate: `
            <div style="font-size:10px; width:100%; text-align:right;
                        margin-right:20px; color:var(--v-secondary-base); font-family:Arial;">
              <span class="pageNumber"></span>
            </div>
          `,
            pageRanges: '1-',
            preferCSSPageSize: true,
            printBackground: true
          };

          await downloadReport(
            reportElement,
            this.generateDocumentNumber(),
            this.reportSummary?.siteName,
            fontOptions
          );
        } catch (error) {
          console.error('PDF 다운로드 실패:', error);
          this.$eventBus.$emit('show-alert', {
            type: 'error',
            message: this.$t('reportSummary.pdfDownloadError')
          });
        } finally {
          this.pdfLoading = false;
        }
      },
      /**
       * 문서번호 생성 (RPT-YYYY-MM-DD-0001 형식)
       */
      generateDocumentNumber() {
        const parts = parseReportDateParts(this.reportSummary?.examineDate);
        const now = new Date();
        const year = parts?.year ?? now.getFullYear();
        const month = String(parts?.month ?? now.getMonth() + 1).padStart(2, '0');
        const day = String(parts?.day ?? now.getDate()).padStart(2, '0');
        const currentReportId = this.reportIds.length > 0 ? this.reportIds[0] : 1;
        const sequenceNumber = String(currentReportId).padStart(6, '0');
        return `RPT-${year}-${month}-${day}-${sequenceNumber}`;
      },
      formatDate(dateString) {
        return formatReportDate(dateString, this.$i18n.locale);
      },

      getTestId(id) {
        return getTestId(id);
      }
    }
  };
</script>

<style scoped>
  .report-dialog {
    /* 높이 제한 제거로 모든 내용이 표시되도록 개선 */
    min-height: auto;
    max-height: none;
    background: transparent !important;
    padding: 30px;
  }

  /* 다이얼로그 컨텐츠 스타일 */
  .examine-report-summary-dialog-content {
    max-height: 90vh !important;
    overflow-y: auto !important;
  }

  /* PDF 출력 시 높이 제한 완전 제거 */
  @media print {
    .report-dialog {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }

    .examine-report-summary-dialog-content {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }
  }

  .v-card {
    background: transparent !important;
    box-shadow: none !important;
  }

  .report-container-table {
    width: 100%;
    border-collapse: collapse;
  }

  .report-container-table td {
    padding: 0;
    vertical-align: top;
  }
  .report-header {
    border-top: 3px solid var(--v-text-base);
    border-bottom: 3px solid var(--v-text-base);
    color: var(--v-text-base);
    font-size: 21px;
    padding: 10px 20px;
  }

  .report-header h1 {
    margin: 0;
    font-size: 21px;
  }

  .report-header img {
    max-width: 80px;
  }

  /* 새로운 헤더 레이아웃 스타일 */
  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
  }

  .logo-section {
    flex: 0 0 auto;
  }

  .company-logo {
    display: block;
    height: 50px;
    width: 50px;
    flex-shrink: 0;
  }

  .title-section {
    flex: 1;
    text-align: center;
  }

  .title-section h1 {
    margin: 0;
    font-size: 24px;
    font-weight: bold;
    color: var(--v-text-base);
  }

  .document-info-section {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 15px;
    min-width: 280px;
  }

  .document-info-table {
    border-collapse: collapse;
    border: 2px solid var(--v-text-base);
    background: var(--v-surface-base);
    font-family: 'Arial', sans-serif;
    box-shadow: 0 1px 3px color-mix(in srgb, var(--v-text-base) 20%, transparent);
  }

  .document-info-table th.info-header {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-weight: bold;
    font-size: 13px;
    padding: 8px 12px;
    border: 1px solid var(--v-text-base);
    text-align: center;
    letter-spacing: 0.5px;
  }

  .document-info-table td.info-label {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-weight: bold;
    font-size: 12px;
    padding: 6px 10px;
    border: 1px solid var(--v-text-base);
    text-align: left;
    width: 80px;
    white-space: nowrap;
  }

  .document-info-table td.info-value {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-family: 'Courier New', monospace;
    font-size: 11px;
    font-weight: normal;
    padding: 6px 12px;
    border: 1px solid var(--v-text-base);
    text-align: left;
    min-width: 180px;
  }

  .buttons-section {
    flex: 0 0 auto;
  }

  :root {
    --mainColor: primary;
    --subColor: secondary;
    --bgColor: var(--v-background-base);
    --fontColor: var(--v-text-base);
  }
</style>

<style lang="scss">
  @media print {
    .report-dialog {
      --v-text-base: #1a1a1a !important;
      --v-surface-base: #ffffff !important;
      --v-background-base: #ffffff !important;
      --v-secondary-base: #424242 !important;
      --v-primary-base: #1976d2 !important;
      background: #ffffff !important;
      color: #1a1a1a !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .report-dialog .report-table table th,
    .report-dialog .document-info-table th.info-header {
      background-color: #f0f0f0 !important;
      color: #1a1a1a !important;
    }

    .report-dialog img {
      background-color: transparent !important;
    }
  }
</style>
